import React from 'react';
import { Box, Typography } from '@mui/material';
import { hexToRgba } from '@europrocurement/l2d-utils';
import { SxProps } from '@mui/system';
import {
    FaOptionIcon,
    lockedInvoiceIcon,
    lockedInvoiceProformaIcon,
    NamedIconsType,
    todoIcon,
} from '@europrocurement/l2d-icons';
import { TextIcon, useModal } from '@europrocurement/flexy-components';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { useToaster } from '@europrocurement/l2d-modules/hooks';
import { ArticlePrefacturation as ArticlePrefacturationType } from '@europrocurement/l2d-modules/providers/CollectionBillingProvider';
import { DragHandle } from '../../../../Offres/components/SortableList/SortableItem/DragHandle';
import ArticleUpdate from '../ArticleUpdate/ArticleUpdate';

import {
    actionsWidth,
    actionsWidthMobile,
    articleToColor,
    dragHandleWidth,
    formatEuro,
    libelleWidth,
    libelleWidthMobile,
    puWidth,
    puWidthMobile,
    quantiteLibelle,
    quantiteWidth,
    quantiteWidthMobile,
    rubFacToColor,
    rubriqueWidth,
    tauxLibelle,
    tauxWidth,
    tauxWidthMobile,
    ttcLibelle,
    ttcWidth,
    ttcWidthMobile,
} from './functions';

export type ArticleProps = {
    article: ArticlePrefacturationType;
    afterSubmit?: () => void;
    onUpdate?: (article: ArticlePrefacturationType) => void;
    onDelete?: (id: number) => void;
    sortabled?: boolean;
    icon?: NamedIconsType;
    iconTooltip?: string;
    sx?: SxProps;
};

const Article: React.FunctionComponent<ArticleProps> = function (props) {
    const {
        afterSubmit,
        onUpdate,
        onDelete,
        article,
        sortabled = false,
        sx,
        icon,
        iconTooltip,
    } = props;

    const { modalActions } = useModal();
    const { prestationArticlesApi } = useFormaliteService();
    const { handleApiResponse } = useToaster();

    const deleteArticle = async () => {
        const deleteRequest = prestationArticlesApi.apiArticlesPrestationIdDelete({
            id: article.id as unknown as string,
        });

        if (onDelete) {
            onDelete(article.id);
        }

        await handleApiResponse(deleteRequest, afterSubmit);
    };

    const montantArticle = (): string | null => {
        if (article.rubFac === 'LIB') {
            return null;
        }
        if (article.prixBase === null) {
            return 'A définir';
        }
        return formatEuro(Number(article.prixBase));
    };

    return (
        <>
            <Box
                sx={{
                    background: articleToColor(
                        article.articleFacturable ? article.articleFacturable : false,
                        article.fMasqueCalcul ? article.fMasqueCalcul : false,
                        article.articleFacture ? article.articleFacture : false,
                    ),
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'none',
                        lg: 'flex',
                        xl: 'flex',
                    },

                    flexDirection: 'row',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '0',
                    borderRadius: '16px',
                    alignItems: 'center',
                    ...sx,
                }}
            >
                <Box sx={{ width: dragHandleWidth }}>{sortabled ? <DragHandle /> : null}</Box>
                <Box
                    sx={{
                        justifyContent: 'center',
                        display: 'flex',
                        width: rubriqueWidth,
                    }}
                >
                    <Box
                        sx={{
                            background: hexToRgba(rubFacToColor(article.rubFac || 'DIV'), 0.08),
                            borderRadius: '12px',
                            textAlign: 'center',
                            padding: '8px 10px',
                            margin: '5px 0px',
                            alignItems: 'center',
                            display: 'inline-block',
                            color: rubFacToColor(article.rubFac || 'DIV'),
                        }}
                    >
                        {article.rubFac?.substring(0, 1).toLocaleUpperCase()}
                        {article.rubFac?.substring(1).toLocaleLowerCase()}
                    </Box>
                </Box>
                <Box
                    sx={{
                        padding: '10px 0px',
                        width: libelleWidth,
                    }}
                >
                    <Typography
                        color="secondary"
                        sx={{ fontWeight: '500' }}
                    >
                        {icon ? (
                            <TextIcon
                                gap="8px"
                                icon={icon}
                                iconTooltip={iconTooltip}
                                sx={{
                                    flexDirection: 'row-reverse',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {article.libelle}
                            </TextIcon>
                        ) : (
                            article.libelle
                        )}
                        {article.articleAjustement ? (
                            <Typography
                                component="span"
                                variant="l2dsubtitles"
                                color="text.gris50"
                                sx={{ display: 'block' }}
                            >
                                Article d&apos;ajustement
                            </Typography>
                        ) : null}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        padding: '10px 0px',
                        width: quantiteWidth,
                    }}
                >
                    <Typography
                        color="text.gris70"
                        sx={{ fontWeight: '500' }}
                    >
                        {article.rubFac !== 'LIB' ? article.quantite : null}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        padding: '10px 10px 10px 0px',
                        width: puWidth,
                        textAlign: 'right',
                    }}
                >
                    <Typography
                        color="text.gris70"
                        sx={{ fontWeight: '500' }}
                    >
                        {montantArticle()}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        padding: '10px 0px',
                        width: tauxWidth,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        color="text.gris70"
                        sx={{ fontWeight: '500' }}
                    >
                        {article.rubFac !== 'LIB' ? `${article.tauxTva} %` : null}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        padding: '10px 0px',
                        width: ttcWidth,
                        textAlign: 'right',
                    }}
                >
                    <Typography
                        color="secondary"
                        sx={{ fontWeight: '600' }}
                    >
                        {article.rubFac !== 'LIB' ? formatEuro(Number(article.ttc)) : null}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', padding: '10px 0px', width: actionsWidth }}>
                    {article.articleModifiable && afterSubmit ? (
                        <Box
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                modalActions.call(
                                    <ArticleUpdate
                                        article={article}
                                        onUpdate={onUpdate}
                                        onDelete={deleteArticle}
                                        afterSubmit={afterSubmit}
                                    />,
                                );
                            }}
                        >
                            <FaOptionIcon
                                {...todoIcon.props}
                                color="text.gris35"
                            />
                        </Box>
                    ) : null}

                    {article.articleFacture && article.articleFactureValidee ? (
                        <FaOptionIcon
                            {...lockedInvoiceIcon.props}
                            color="text.gris35"
                            tooltip={article.articleFactureNumero}
                        />
                    ) : null}
                    {article.articleFacture && !article.articleFactureValidee ? (
                        <FaOptionIcon
                            {...lockedInvoiceProformaIcon.props}
                            color="text.gris35"
                            tooltip={article.articleFactureNumero}
                        />
                    ) : null}

                    {!article.articleFacture &&
                    !article.articleModifiable &&
                    article.articleSupprimable ? (
                        <Box
                            sx={{ cursor: 'pointer' }}
                            onClick={deleteArticle}
                        >
                            <FaOptionIcon
                                icon="trash"
                                color="text.gris35"
                            />
                        </Box>
                    ) : null}
                </Box>
            </Box>

            <Box
                sx={{
                    background: articleToColor(
                        article.articleFacturable ? article.articleFacturable : false,
                        article.fMasqueCalcul ? article.fMasqueCalcul : false,
                        article.articleFacture ? article.articleFacture : false,
                    ),
                    display: {
                        xs: 'flex',
                        sm: 'flex',
                        md: 'flex',
                        lg: 'none',
                        xl: 'none',
                    },

                    flexDirection: 'column',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '0',
                    borderRadius: '16px',
                    alignItems: 'center',
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ width: dragHandleWidth }}>{sortabled ? <DragHandle /> : null}</Box>
                    <Box
                        sx={{
                            justifyContent: 'center',
                            display: 'flex',
                            width: rubriqueWidth,
                        }}
                    >
                        <Box
                            sx={{
                                background: hexToRgba(rubFacToColor(article.rubFac || 'DIV'), 0.08),
                                borderRadius: '12px',
                                textAlign: 'center',
                                padding: '8px 10px',
                                margin: '5px 0px',
                                alignItems: 'center',
                                display: 'inline-block',
                                color: rubFacToColor(article.rubFac || 'DIV'),
                            }}
                        >
                            {article.rubFac?.substring(0, 1).toLocaleUpperCase()}
                            {article.rubFac?.substring(1).toLocaleLowerCase()}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: '10px 0px',
                            width: libelleWidthMobile,
                        }}
                    >
                        <Typography
                            color="secondary"
                            sx={{ fontWeight: '500' }}
                        >
                            {article.articleAjustement ? (
                                <>
                                    {article.libelle}{' '}
                                    <Typography
                                        component="span"
                                        variant="l2dsubtitles"
                                        color="text.gris50"
                                        sx={{ display: 'block' }}
                                    >
                                        Article d&apos;ajustement
                                    </Typography>
                                </>
                            ) : (
                                article.libelle
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            padding: '10px 0px',
                            width: quantiteWidthMobile,
                        }}
                    >
                        <Typography
                            color="text.gris70"
                            sx={{ fontWeight: '500' }}
                        >
                            {article.rubFac !== 'LIB'
                                ? `${quantiteLibelle} : ${article.quantite}`
                                : null}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            padding: '10px 0px',
                            width: puWidthMobile,
                        }}
                    >
                        <Typography
                            color="text.gris70"
                            sx={{ fontWeight: '500' }}
                        >
                            {montantArticle()}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            padding: '10px 0px',
                            width: tauxWidthMobile,
                        }}
                    >
                        <Typography
                            color="text.gris70"
                            sx={{ fontWeight: '500' }}
                        >
                            {article.rubFac !== 'LIB'
                                ? `${tauxLibelle} : ${article.tauxTva} %`
                                : null}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            padding: '10px 0px',
                            width: ttcWidthMobile,
                        }}
                    >
                        <Typography
                            color="secondary"
                            sx={{ fontWeight: '600' }}
                        >
                            {article.rubFac !== 'LIB'
                                ? `${ttcLibelle} : ${formatEuro(Number(article.ttc))}`
                                : null}
                        </Typography>
                    </Box>
                    <Box
                        sx={{ textAlign: 'right', padding: '10px 0px', width: actionsWidthMobile }}
                    >
                        {article.articleFacturable && onUpdate ? (
                            <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    modalActions.call(
                                        <ArticleUpdate
                                            article={article}
                                            onUpdate={onUpdate}
                                            onDelete={deleteArticle}
                                            afterSubmit={afterSubmit}
                                        />,
                                    );
                                }}
                            >
                                <FaOptionIcon
                                    {...todoIcon.props}
                                    color="text.gris35"
                                />
                            </Box>
                        ) : null}
                        {article.articleFacture ? (
                            <FaOptionIcon
                                {...lockedInvoiceIcon.props}
                                color="text.gris35"
                                tooltip={article.articleFactureNumero}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Article;
